import { APP_INITIALIZER, FactoryProvider, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationError, NavigationStart } from '@angular/router';

import { filter, take } from 'rxjs';

import { CommonRoutes, GlobalRegistrationRoutes } from '@hdms/shared/common-types';
import { UserContextService } from '@hdms/shared/data-access';
import {
  EnvironmentService,
  FeatureToggleService,
  PlatformService,
  RouterHelperService,
  SERVER_RESPONSE,
  StorageService,
  disableSsrCaching
} from '@hdms/shared/util-core';

import { EmptyComponent } from '../components/empty.component';

import { encodeParens } from './custom-url.serializer';

@Injectable({
  providedIn: 'root'
})
export class RouterConfigService {
  private readonly response = inject(SERVER_RESPONSE, { optional: true });
  private readonly routerHelper = inject(RouterHelperService);
  private readonly envService = inject(EnvironmentService);
  private readonly platform = inject(PlatformService);
  private readonly userContextService = inject(UserContextService);
  private readonly storage = inject(StorageService);
  private readonly isRegistrationRedirectForNgcEnabled = inject(FeatureToggleService).getToggleById(
    'ldt_registration_redirect_for_ngc'
  );
  private readonly registrationRoute = `/${GlobalRegistrationRoutes.BASE}/${GlobalRegistrationRoutes.ACCOUNT}`;

  private readonly router = this.routerHelper.router;

  constructor() {
    this.preventInitialNavigationTimeout();
    this.addRouterErrorHandler();
    this.setPageFrameRouteConfig();
    if (
      this.userContextService.isLoggedIn &&
      this.userContextService.isPendingRegistration &&
      this.isRegistrationRedirectForNgcEnabled
    ) {
      this.routePartialRegisteredUser();
    }
  }

  /**
   * If app runs in a host context rendering page frame only, disable routing
   * by replacing routing config with one empty catch-all route.
   * @private
   */
  private setPageFrameRouteConfig(): void {
    if (this.envService.pageFrameOnly) {
      this.router.resetConfig([
        {
          path: '**',
          component: EmptyComponent
        }
      ]);
    }
  }

  /**
   * In SSR `initialNavigation` has to be set to 'enabledBlocking', which causes
   * server timeout if initial navigation will be cancelled by e.g. a RouteGuard.
   * In order to circumvent timeout issues in SSR just navigate to `empty` route.
   * @private
   */
  private preventInitialNavigationTimeout(): void {
    if (this.platform.isServer) {
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationCancel && event.id === 1),
          take(1),
          takeUntilDestroyed()
        )
        .subscribe(_ => this.router.navigate([CommonRoutes.EMPTY]));
    }
  }

  /**
   * In case of a router error navigate to same url by encoding parenthesis,
   * as such error happened likely because of malformed outlet url syntax.
   * That way likely a 404 page will be displayed, which is intended.
   * If for some reason the target url still errors out even after it
   * has been url-encoded, then do a final redirect to generic error page.
   *
   * On server-side, prevent caching in case of an occurring error
   * by setting `routeData.cacheConfig.disabled` on the response locals.
   * @private
   */
  private addRouterErrorHandler(): void {
    let previousErrorRoute: string;

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationError),
        takeUntilDestroyed()
      )
      .subscribe(_ => {
        if (this.response) {
          disableSsrCaching(this.response);
        }

        let redirectUrl = encodeParens(this.routerHelper.finalUrlRaw);
        let skipLocationChange = false;

        if (previousErrorRoute === redirectUrl) {
          redirectUrl = CommonRoutes.GENERIC_ERROR;
          skipLocationChange = true;
        }
        previousErrorRoute = redirectUrl;

        this.router.navigateByUrl(redirectUrl, { replaceUrl: true, skipLocationChange });
      });
  }

  /**
   * If a partially registered user logs in, redirect them to the Registration flow rather than returning them to the page
   * they logged in from. However, if they navigate away from Registration flow after the initial redirect, respect the
   * user's intent and do not repeat redirect.
   * @private
   */
  private routePartialRegisteredUser(): void {
    this.router.events
      .pipe(
        filter<NavigationStart>(e => e instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(({ url }) => {
        const previouslyRedirectedtoReg = this.storage.getItem('wasRedirectedtoReg');
        if (url !== this.registrationRoute && !previouslyRedirectedtoReg && this.isRegistrationRedirectForNgcEnabled) {
          this.router.navigateByUrl(this.registrationRoute);
        }
      });
  }
}

export const ROUTER_CONFIG_SERVICE_PROVIDER: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory: () => () => null,
  deps: [RouterConfigService],
  multi: true
};
